import { useEffect, useMemo, useRef, useState } from "react";
import { EnhancedMultiSelect } from "@/components/ui/enhanced-multiselect";
import type { EnhancedSelectOption, FieldConfig } from "../types";

interface MultiSelectFieldProps<T> {
  field: FieldConfig<T>;
  value: string[];
  onChange: (value: string[]) => void;
  disabled?: boolean;
  id: string;
  placeholder?: string;
  "aria-describedby"?: string;
  "aria-invalid"?: boolean;
}

export function MultiSelectField<T>({
  field,
  value,
  onChange,
  disabled,
  id,
  placeholder,
  "aria-describedby": ariaDescribedBy,
  "aria-invalid": ariaInvalid,
}: MultiSelectFieldProps<T>) {
  const loadOptions = field.loadOptions;

  // ---- Eager mode (no loadOptions): render the static option list. ----
  const eagerOptions = Array.isArray(field.selectOptions) ? field.selectOptions : [];

  // ---- Async mode (loadOptions): server-side search, fetched only while the
  // dropdown is open so the form mounts without eagerly loading the full list. ----
  const [open, setOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [results, setResults] = useState<EnhancedSelectOption[]>(field.initialOptions ?? []);
  const [isLoading, setIsLoading] = useState(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Accumulating label cache so selected items keep their label across searches.
  const cacheRef = useRef<Map<string, EnhancedSelectOption>>(new Map());

  useEffect(() => {
    for (const opt of field.initialOptions ?? []) {
      cacheRef.current.set(String(opt.value), opt);
    }
  }, [field.initialOptions]);

  useEffect(() => {
    if (!loadOptions || !open) return;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    let cancelled = false;
    setIsLoading(true);
    debounceRef.current = setTimeout(() => {
      loadOptions(searchTerm)
        .then((next) => {
          if (cancelled) return;
          for (const opt of next) cacheRef.current.set(String(opt.value), opt);
          setResults(next);
        })
        .catch(() => {
          if (!cancelled) setResults([]);
        })
        .finally(() => {
          if (!cancelled) setIsLoading(false);
        });
    }, 250);
    return () => {
      cancelled = true;
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [loadOptions, open, searchTerm]);

  // Show the search results plus any currently-selected items not in the result
  // set (resolved from the cache) so their checkbox + label stay visible.
  const asyncOptions = useMemo<EnhancedSelectOption[]>(() => {
    const merged = [...results];
    const present = new Set(merged.map((o) => String(o.value)));
    for (const v of value ?? []) {
      if (!present.has(v)) {
        merged.push(cacheRef.current.get(v) ?? { value: v, label: v });
      }
    }
    return merged;
  }, [results, value]);

  // Carry through every option field (including color/hideCheckbox/emoji that
  // EnhancedMultiSelect renders) and only normalize value/suggested, so static
  // options keep their intended rendering.
  const options = (loadOptions ? asyncOptions : eagerOptions).map((opt) => ({
    ...opt,
    value: String(opt.value),
    suggested: opt.suggested ?? false,
  }));

  return (
    <EnhancedMultiSelect
      id={id}
      aria-describedby={ariaDescribedBy}
      aria-invalid={ariaInvalid}
      placeholder={placeholder}
      disabled={disabled}
      value={value || []}
      onChange={onChange}
      options={options}
      onSearchChange={loadOptions ? setSearchTerm : undefined}
      onOpenChange={loadOptions ? setOpen : undefined}
      isLoading={loadOptions ? isLoading : undefined}
    />
  );
}
